import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLinkedInAccount } from '../hooks/useLinkedInAccount';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Plus,
  Edit,
  Trash2,
  Clock,
  User,
  Menu,
  Filter,
  X,
  Eye,
  // ExternalLink,
  ThumbsUp,
  Repeat2,
  MessageCircle,
  MoreHorizontal
} from 'lucide-react';
import Sidebar from './Sidebar';
import Header from './Header';

const SchedulePostsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('scheduled-posts');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showPostPreview, setShowPostPreview] = useState(false);
  const [previewPost, setPreviewPost] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({
    scheduled: false,
    posted: false,
    upcoming: false
  });
  const { accountId, accountType } = useLinkedInAccount();

  // useEffect(() => {
  //   fetchScheduledPosts();
  //   const interval = setInterval(fetchScheduledPosts, 30000);
  //   return () => clearInterval(interval);
  // }, [currentDate]);
const fetchScheduledPosts = useCallback(async () => {
  try {
    setLoading(true);
    
    // ✅ GET userId FROM localStorage
    const userId = localStorage.getItem('userId');
    
    // ✅ Check if both userId and accountId exist
    if (!userId || !accountId) {
      console.log('[SCHEDULE] Missing userId or accountId:', { userId, accountId });
      setScheduledPosts([]);
      setLoading(false);
      return;
    }

    console.log('[SCHEDULE] Fetching posts for:', { 
      userId: parseInt(userId), 
      accountId,
      month: currentDate.getMonth() + 1,
      year: currentDate.getFullYear() 
    });

    // ✅ FIXED URL - Removed 'webhook-test'
    const response = await fetch('https://n8n.acespireconsulting.com/webhook/webhook/get-scheduled-posts', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: parseInt(userId),
        linkedinAccountId: accountId,
        month: currentDate.getMonth() + 1,
        year: currentDate.getFullYear(),
        filters: selectedFilters
      })
    });

    console.log('[SCHEDULE] Response status:', response.status);

    if (response.ok) {
      const data = await response.json();
      console.log('[SCHEDULE] Received data:', data);
      
      let posts = Array.isArray(data) ? data : (data.posts || []);
      
      // Apply client-side filtering if any filters are selected
      const hasActiveFilters = selectedFilters.scheduled || selectedFilters.posted || selectedFilters.upcoming;
      
      if (hasActiveFilters) {
        posts = posts.filter(post => {
          const now = new Date();
          const postDate = new Date(post.scheduled_time || post.posted_time);
          
          // If no filters selected, show all
          if (!selectedFilters.scheduled && !selectedFilters.posted && !selectedFilters.upcoming) {
            return true;
          }
          
          // Check each filter
          let matchesFilter = false;
          
          if (selectedFilters.scheduled && post.status === 'scheduled') {
            matchesFilter = true;
          }
          
          if (selectedFilters.posted && post.status === 'posted') {
            matchesFilter = true;
          }
          
          if (selectedFilters.upcoming && post.status === 'scheduled' && postDate > now) {
            matchesFilter = true;
          }
          
          return matchesFilter;
        });
      }
      
      const transformedPosts = posts.map(post => {
        const postDateTime = new Date(post.scheduled_time || post.posted_time);
        
        return {
          ...post,
          date: postDateTime,
          title: post.prompt || post.content?.substring(0, 50) + '...',
          color: post.status === 'posted' ? 'bg-green-100 border-green-300' : 'bg-yellow-100 border-yellow-300',
          time: postDateTime.toLocaleTimeString('en-IN', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true,
            timeZone: 'Asia/Kolkata'
          }),
          displayDate: postDateTime.toLocaleDateString('en-IN', {
            timeZone: 'Asia/Kolkata'
          })
        };
      });
      
      console.log('[SCHEDULE] Transformed posts:', transformedPosts.length);
      setScheduledPosts(transformedPosts);
    } else {
      console.error('[SCHEDULE] Response not OK:', response.status);
      const errorText = await response.text();
      console.error('[SCHEDULE] Error response:', errorText);
    }
  } catch (error) {
    console.error('[SCHEDULE] Error fetching scheduled posts:', error);
  } finally {
    setLoading(false);
  }
}, [currentDate, selectedFilters, accountId]); // ✅ ADDED accountId to dependencies
// Add listener for account changes
useEffect(() => {
  const handleAccountChange = () => {
    fetchScheduledPosts();
  };

  window.addEventListener('accountChanged', handleAccountChange);
  return () => window.removeEventListener('accountChanged', handleAccountChange);
}, [fetchScheduledPosts]);
// ← ADD selectedFilters dependency
useEffect(() => {
  fetchScheduledPosts();
  // const interval = setInterval(fetchScheduledPosts, 60000);
  // return () => clearInterval(interval);
}, [fetchScheduledPosts]);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    return { firstDay, daysInMonth };
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const getPostsForDate = (day) => {
    return scheduledPosts.filter(post => {
      const postDate = post.date;
      return postDate.getDate() === day &&
             postDate.getMonth() === currentDate.getMonth() &&
             postDate.getFullYear() === currentDate.getFullYear();
    });
  };

  const handleAddNewPost = () => {
    navigate('/create-post');
  };

  const handleDateDoubleClick = (day) => {
  const posts = getPostsForDate(day);
  if (posts.length === 1) {
    setPreviewPost(posts[0]);
    setShowPostPreview(true);
  }
  // Don't auto-show preview for multiple posts - let user click individual posts
};

const handlePostClick = (post, event) => {
  event.stopPropagation();
  
  // If clicking the same post, show preview
  if (selectedPost?.id === post.id) {
    setPreviewPost(post);
    setShowPostPreview(true);
  } else {
    // Otherwise just select it
    setSelectedPost(post);
  }
};

// Add a new function to handle double-click on individual posts
const handlePostDoubleClick = (post, event) => {
  event.stopPropagation();
  setPreviewPost(post);
  setShowPostPreview(true);
};

  const handleEditPost = () => {
    if (selectedPost) {
      navigate(`/create-post?edit=${selectedPost.id}`);
    }
  };

  const handleReschedule = () => {
    if (selectedPost) {
      navigate(`/create-post?reschedule=${selectedPost.id}`);
    }
  };

  const handleDeletePost = async () => {
  if (!selectedPost) return;
  
  if (window.confirm('Are you sure you want to delete this scheduled post?')) {
    try {
      const response = await fetch('https://n8n.acespireconsulting.com/webhook/delete-scheduled-post', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postId: selectedPost.id })
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        // Success - show message and refresh
        alert('Post deleted successfully!');
        
        // Close preview modal if open
        setShowPostPreview(false);
        
        // Clear selected post
        setSelectedPost(null);
        
        // Refresh the calendar
        fetchScheduledPosts();
      } else {
        // Error from backend
        alert(result.message || 'Failed to delete post. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Network error: Failed to delete post. Please check your connection and try again.');
    }
  }
};

  const toggleFilter = (filterName) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };

  const applyFilters = () => {
    fetchScheduledPosts();
  };

  const { firstDay, daysInMonth } = getDaysInMonth(currentDate);
  const blanks = Array(firstDay).fill(null);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  // Post Preview Modal Component
  const PostPreviewModal = () => {
  if (!showPostPreview || !previewPost) return null;

  // Add delete handler specifically for the modal
  const handleDeleteFromModal = async () => {
    if (!previewPost) return;
    
    if (window.confirm('Are you sure you want to delete this scheduled post?')) {
      try {
        const response = await fetch('https://n8n.acespireconsulting.com/webhook/delete-scheduled-post', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ postId: previewPost.id })
        });
        
        const result = await response.json();
        
        if (response.ok && result.success) {
          alert('Post deleted successfully!');
          
          // Close the modal
          setShowPostPreview(false);
          
          // Clear selected post
          setSelectedPost(null);
          
          // Refresh calendar
          fetchScheduledPosts();
        } else {
          alert(result.message || 'Failed to delete post. Please try again.');
        }
      } catch (error) {
        console.error('Error deleting post:', error);
        alert('Network error: Failed to delete post. Please check your connection and try again.');
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Post Preview</h2>
          <button
            onClick={() => setShowPostPreview(false)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Post Preview Card */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mr-3">
                <span className="text-white text-sm font-semibold">AC</span>
              </div>
              <div>
                <h3 className="font-semibold text-sm">Acespire Consulting</h3>
                <p className="text-xs text-gray-500">
                  {previewPost.status === 'posted' ? '2h' : 'Scheduled'} • 3rd
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button className="text-blue-600 text-sm hover:underline">Follow</button>
              <button className="text-gray-400">
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="mb-3">
            <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line">
              {previewPost.content}
            </p>
            <button className="text-blue-600 text-sm hover:underline mt-1">See translation</button>
          </div>

          {previewPost.media_url && (
            <div className="mb-3">
              <img 
                src={previewPost.media_url} 
                alt="Post media" 
                className="w-full rounded-lg"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <p className="text-xs text-gray-600 mt-2">
                Generated with {previewPost.tone_style || 'professional'} tone for {previewPost.post_objective || 'engagement'} objective
              </p>
            </div>
          )}

          <div className="flex items-center justify-between text-gray-500 text-xs border-t pt-3">
            <div className="flex items-center space-x-4">
              <button className="flex items-center hover:text-blue-600">
                <ThumbsUp className="w-4 h-4 mr-1" />
                0
              </button>
              <button className="flex items-center hover:text-green-600">
                <Repeat2 className="w-4 h-4 mr-1" />
                0
              </button>
              <button className="flex items-center hover:text-purple-600">
                <MessageCircle className="w-4 h-4 mr-1" />
                0
              </button>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          {previewPost.status === 'scheduled' && (
            <button
              onClick={handleDeleteFromModal} // ← UPDATED: Use dedicated modal handler
              className="flex-1 flex items-center justify-center px-4 py-2 text-red-600 border border-red-600 rounded-lg hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </button>
          )}
        </div>

        <button
          onClick={() => setShowPostPreview(false)}
          className="w-full mt-4 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
        >
          Close
        </button>
      </div>
    </div>
  );
};

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />
      
       <div className="flex-1 flex flex-col min-w-0 lg:ml-64">
        
        
        <div className="flex-1 p-4 lg:p-8 overflow-y-auto">
          {/* ✅ ADD HEADER HERE */}
        <Header setSidebarOpen={setSidebarOpen} />
          {/* REMOVE THE OLD HEADER - Delete these lines: */}
          {/* <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <button 
                className="lg:hidden mr-3 p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-gray-800">Scheduled Posts</h1>
                <p className="text-sm text-gray-600">
                  Manage your content calendar • 
                  <span className="ml-2 inline-flex items-center">
                    <span className="w-3 h-3 bg-yellow-300 rounded-full mr-1"></span>
                    Scheduled
                    <span className="w-3 h-3 bg-green-300 rounded-full ml-3 mr-1"></span>
                    Posted
                  </span>
                </p>
              </div>
            </div>
            <button className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
              <User className="w-4 h-4" />
            </button>
          </div> */}

          {/* ✅ ADD NEW PAGE TITLE */}
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-gray-800">Scheduled Posts</h1>
            <p className="text-sm text-gray-600">
              Manage your content calendar • 
              <span className="ml-2 inline-flex items-center">
                <span className="w-3 h-3 bg-yellow-300 rounded-full mr-1"></span>
                Scheduled
                <span className="w-3 h-3 bg-green-300 rounded-full ml-3 mr-1"></span>
                Posted
              </span>
            </p>
          </div>

          {/* Calendar and Filters Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Calendar Section */}
            <div className="lg:col-span-3 bg-white rounded-lg shadow-sm p-6">
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-800">
                  {months[currentDate.getMonth()]} {currentDate.getFullYear()}
                </h2>
                <div className="flex items-center space-x-2">
                  {loading && (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                  )}
                  <button
                    onClick={() => navigateMonth(-1)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setCurrentDate(new Date())}
                    className="px-4 py-2 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    Today
                  </button>
                  <button
                    onClick={() => navigateMonth(1)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-2">
                {/* Day Headers */}
                {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(day => (
                  <div key={day} className="text-center text-xs font-semibold text-gray-500 py-2">
                    {day}
                  </div>
                ))}

                {/* Empty cells */}
                {blanks.map((_, index) => (
                  <div key={`blank-${index}`} className="aspect-square" />
                ))}

                {/* Calendar Days */}
                {days.map(day => {
                  const posts = getPostsForDate(day);
                  const isToday = 
                    day === new Date().getDate() &&
                    currentDate.getMonth() === new Date().getMonth() &&
                    currentDate.getFullYear() === new Date().getFullYear();

                  return (
                    <div
                      key={day}
                      onDoubleClick={() => handleDateDoubleClick(day)}
                      className={`aspect-square border rounded-lg p-2 cursor-pointer ${
                        isToday ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                      } hover:border-gray-300 transition-colors overflow-hidden`}
                    >
                      <div className="text-sm font-medium text-gray-700 mb-1">{day}</div>
                      <div className="space-y-1 overflow-y-auto max-h-24">
                        {posts.map(post => (
                          <div
                            key={post.id}
                            className={`${post.color} rounded px-2 py-1 text-xs cursor-pointer transition-all border ${
                              selectedPost?.id === post.id ? 'ring-2 ring-blue-500' : ''
                            }`}
                            onClick={(e) => handlePostClick(post, e)}
                            title={post.title}
                          >
                            <div className="font-medium truncate">{post.title}</div>
                            <div className="text-gray-600 flex items-center text-xs">
                              <Clock className="w-3 h-3 mr-1" />
                              {post.time}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Filters and Actions Sidebar */}
            <div className="lg:col-span-1 space-y-4">
              {/* Filters */}
              {/* Filters */}
<div className="bg-white rounded-lg shadow-sm p-4">
  <div className="flex items-center justify-between mb-4">
    <h3 className="font-semibold text-gray-800">Filters</h3>
    <Filter className="w-4 h-4 text-gray-500" />
  </div>
  
  <div className="space-y-3">
    <label className="flex items-center space-x-2 cursor-pointer">
      <input
        type="checkbox"
        checked={selectedFilters.scheduled}
        onChange={() => toggleFilter('scheduled')}
        className="w-4 h-4 text-blue-600 rounded"
      />
      <span className="text-sm text-gray-700">Scheduled Only</span>
    </label>
    
    <label className="flex items-center space-x-2 cursor-pointer">
      <input
        type="checkbox"
        checked={selectedFilters.posted}
        onChange={() => toggleFilter('posted')}
        className="w-4 h-4 text-blue-600 rounded"
      />
      <span className="text-sm text-gray-700">Posted Only</span>
    </label>
    
    {/* <label className="flex items-center space-x-2 cursor-pointer">
      <input
        type="checkbox"
        checked={selectedFilters.upcoming}
        onChange={() => toggleFilter('upcoming')}
        className="w-4 h-4 text-blue-600 rounded"
      />
      <span className="text-sm text-gray-700">Upcoming Only</span>
    </label> */}
  </div>

  <div className="flex space-x-2 mt-4">
    <button 
      onClick={applyFilters}
      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
    >
      Apply
    </button>
    <button 
      onClick={() => {
        setSelectedFilters({ scheduled: false, posted: false, upcoming: false });
      }}
      className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
    >
      Clear
    </button>
  </div>
</div>

              {/* Action Buttons */}
<div className="bg-white rounded-lg shadow-sm p-4 space-y-3">
  <button
    onClick={() => {
      setPreviewPost(selectedPost);
      setShowPostPreview(true);
    }}
    disabled={!selectedPost}
    className={`w-full flex items-center justify-center px-4 py-2 border rounded-lg transition-colors text-sm ${
      !selectedPost
        ? 'border-gray-300 text-gray-400 cursor-not-allowed' 
        : 'border-blue-600 text-blue-600 hover:bg-blue-50'
    }`}
  >
    <Eye className="w-4 h-4 mr-2" />
    View Post
  </button>
  
  <button
    onClick={handleEditPost}
    disabled={!selectedPost || selectedPost.status === 'posted'}
    className={`w-full flex items-center justify-center px-4 py-2 border rounded-lg transition-colors text-sm ${
      !selectedPost || selectedPost.status === 'posted'
        ? 'border-gray-300 text-gray-400 cursor-not-allowed' 
        : 'border-blue-600 text-blue-600 hover:bg-blue-50'
    }`}
  >
    <Edit className="w-4 h-4 mr-2" />
    Edit Post
  </button>
  
  <button
    onClick={handleReschedule}
    disabled={!selectedPost || selectedPost.status === 'posted'}
    className={`w-full flex items-center justify-center px-4 py-2 border rounded-lg transition-colors text-sm ${
      !selectedPost || selectedPost.status === 'posted'
        ? 'border-gray-300 text-gray-400 cursor-not-allowed' 
        : 'border-blue-600 text-blue-600 hover:bg-blue-50'
    }`}
  >
    <Clock className="w-4 h-4 mr-2" />
    Reschedule
  </button>
</div>

              {/* Add New Post Button */}
              <button
                onClick={handleAddNewPost}
                className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add New Post
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Post Preview Modal */}
      <PostPreviewModal />
    </div>
  );
};

export default SchedulePostsPage;